import { Flag, Plus, Trash2 } from "lucide-react";
import { DatePicker } from "@/components/ui/DatePicker";

export interface MilestoneDraft {
  label: string;
  due_date: string;
}

/** Latest milestone date (ISO strings sort chronologically), or null. Milestones without a
 *  date are ignored — the deadline can't be validated against a milestone that has none. */
export function latestMilestoneDate(milestones: { due_date: string }[]): string | null {
  const dates = milestones.map((m) => m.due_date).filter(Boolean);
  return dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : null;
}

export const DEADLINE_BEFORE_MILESTONE_MSG =
  "Termin realizacji nie może być wcześniejszy niż najpóźniejszy kamień milowy";

/**
 * Controlled editor for a list of intermediate milestones ("nazwa → termin"). Reused on the
 * requisition form and in the approval modal (where the approver can still edit them before
 * they materialise as order checkpoints).
 */
export function MilestonesEditor({
  value,
  onChange,
  title = "Kamienie milowe (terminy pośrednie)",
}: {
  value: MilestoneDraft[];
  onChange: (next: MilestoneDraft[]) => void;
  title?: string;
}) {
  const update = (i: number, patch: Partial<MilestoneDraft>) =>
    onChange(value.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...value, { label: "", due_date: "" }]);

  const inputCls =
    "w-full rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 " +
    "px-2.5 py-1.5 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 " +
    "focus:border-[var(--color-primary)] focus:bg-white dark:focus:bg-gray-900 focus:outline-none " +
    "focus:ring-2 focus:ring-[var(--color-primary)]/20 transition-colors";

  return (
    <div>
      <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1.5 mb-2">
        <Flag size={15} className="text-[var(--color-primary)]" /> {title}
        {value.length > 0 && (
          <span className="ml-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-[var(--color-primary)]/10 px-1.5 text-[11px] font-medium text-[var(--color-primary)]">
            {value.length}
          </span>
        )}
      </p>

      <ul className="space-y-2">
        {value.map((m, i) => (
          <li
            key={i}
            className="group flex items-center gap-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-2 shadow-sm"
          >
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[var(--color-primary)]/10 text-[11px] font-bold text-[var(--color-primary)]">
              {i + 1}
            </span>
            <input
              type="text"
              value={m.label}
              onChange={(e) => update(i, { label: e.target.value })}
              placeholder="np. Wysłanie koncepcji do ZE"
              className={`flex-1 min-w-0 ${inputCls}`}
            />
            <DatePicker
              value={m.due_date}
              onChange={(v) => update(i, { due_date: v })}
              className="w-[9.5rem] shrink-0"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="shrink-0 rounded-md p-1.5 text-gray-300 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 transition-colors"
              title="Usuń kamień"
            >
              <Trash2 size={15} />
            </button>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={add}
        className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] hover:bg-[var(--color-primary)]/5 transition-colors"
      >
        <Plus size={15} /> Dodaj kamień milowy
      </button>
    </div>
  );
}
